// @ts-strict-ignore
import React from 'react';

import type { MatrixModel } from 'flow/store/engine/types';
import type { Axis } from '../../AxisHeaderDropdown';
import {
  getDefaultRangeText,
  getUnboundedHighRangeText,
  getUnboundedLowRangeText,
  isValueUnboundedHighRange,
  isValueUnboundedLowRange,
} from '../helpers';
import {
  StyledBox, StyledGrayText,
} from './styles';
import { AxisHeaderTextInput } from './AxisHeaderTextInput';

export interface AxisHeaderRangeInputProps {
  axis: Axis;
  axisOrder: MatrixModel['axis']['x' | 'y']['order'];
  values: {
    left: { index: number; value: number };
    right: { index: number; value: number };
  };
  onChange: ({ axis, index, value }) => void;
  isLastBucket: boolean;
}

export default function AxisHeaderRangeInput({
  axis, axisOrder, values, onChange, isLastBucket,
}: AxisHeaderRangeInputProps) {
  const { left, right } = values;
  const [leftValue, setLeftValue] = React.useState(left.value);
  const [rightValue, setRightValue] = React.useState(right.value);

  const handleOnInput = ({ target: { value, id } }) => {
    if (+id === left.index) setLeftValue(value);
    else setRightValue(value);
  };

  const handleOnBlur = () => {
    if (leftValue !== left.value) {
      onChange({ axis, index: left.index, value: +leftValue });
    }
    if (rightValue !== right.value) {
      onChange({ axis, index: right.index, value: +rightValue });
    }
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', 'Tab'].includes(e.key)) {
      handleOnBlur();
    }
  };

  if (isValueUnboundedHighRange(left.value)) {
    return (
      <StyledBox>
        <StyledGrayText>
          {getUnboundedHighRangeText(axis, axisOrder)}
        </StyledGrayText>
        <AxisHeaderTextInput
          autoComplete="off"
          type="number"
          placeholder="value"
          value={rightValue}
          id={`${right.index}`}
          onChange={handleOnInput}
          onBlur={handleOnBlur}
          onKeyDown={handleOnKeyDown}
        />
      </StyledBox>
    );
  }

  if (isValueUnboundedHighRange(right.value)) {
    const isFirstColumnInAsc = axisOrder === 'asc'
      && left.index === 0;

    return (
      <StyledBox>
        {isFirstColumnInAsc && (
          <AxisHeaderTextInput
            autoComplete="off"
            type="number"
            placeholder="value"
            value={leftValue}
            id={`${left.index}`}
            onChange={handleOnInput}
            onBlur={handleOnBlur}
            onKeyDown={handleOnKeyDown}
          />
        )}
        <StyledGrayText>
          {!isFirstColumnInAsc && `${left.value ?? 'value'} `}
          {getUnboundedHighRangeText(axis, axisOrder)}
        </StyledGrayText>
      </StyledBox>
    );
  }

  if (isValueUnboundedLowRange(left.value)) {
    return (
      <StyledBox>
        <StyledGrayText>
          {getUnboundedLowRangeText(axis, axisOrder)}
        </StyledGrayText>
        <AxisHeaderTextInput
          autoComplete="off"
          type="number"
          placeholder="value"
          value={rightValue}
          id={`${right.index}`}
          onChange={handleOnInput}
          onBlur={handleOnBlur}
          onKeyDown={handleOnKeyDown}
        />
      </StyledBox>
    );
  }

  if (isValueUnboundedLowRange(right.value)) {
    const isFirstColumnInDesc = axisOrder === 'desc'
    && left.index === 0;

    return (
      <StyledBox>
        {isFirstColumnInDesc && (
          <AxisHeaderTextInput
            autoComplete="off"
            type="number"
            placeholder="value"
            value={leftValue}
            id={`${left.index}`}
            onChange={handleOnInput}
            onBlur={handleOnBlur}
            onKeyDown={handleOnKeyDown}
          />
        )}
        <StyledGrayText>
          {!isFirstColumnInDesc && `${left.value ?? 'value'} `}
          {getUnboundedLowRangeText(axis, axisOrder)}
        </StyledGrayText>
      </StyledBox>
    );
  }

  // default:
  return (
    <StyledBox>
      {left.index === 0 && (
        <AxisHeaderTextInput
          autoComplete="off"
          type="number"
          placeholder="value"
          value={leftValue}
          id={`${left.index}`}
          onChange={handleOnInput}
          onBlur={handleOnBlur}
          onKeyDown={handleOnKeyDown}
        />
      )}
      <StyledGrayText>
        {left.index !== 0 && `${left.value ?? 'value'} `}
        {getDefaultRangeText(axis, axisOrder, isLastBucket)}
      </StyledGrayText>
      <AxisHeaderTextInput
        autoComplete="off"
        type="number"
        placeholder="value"
        value={rightValue}
        id={`${right.index}`}
        onChange={handleOnInput}
        onBlur={handleOnBlur}
        onKeyDown={handleOnKeyDown}
      />
    </StyledBox>
  );
}
